 "use client";
import React, { Component, createContext } from 'react';
import { getHostUrl } from "../utils/UrlManager";

export const CustomerContext = createContext(null);

class CustomerContextProvider extends Component {

    /**
     * Number of tries to load customer before showing
     * error message
     * @type {number}
     */
    loadCustomerRetries = 5;

    /**
     * Component Mount
     */
    componentDidMount() {
        this.preloadCustomerToken();
        this.resetEditingSubscriptionOnReload();
    }

    /**
     * Constructor
     * @param props
     */
    constructor(props) {
        super(props);
        this.state = {
            customer: undefined,
            token: '',
            loadingCustomerData: true,
            customerLoadError: false,
            customerDetails: {
                dob: '',
                email: '',
                name: '',
                phone: '',
            },
            customerAddress: {
                streetOne: '',
                streetTwo: '' ,
                city: '',
                country: '',
                postcode: '',
                county: '',
            },
            payment: {
                cards: [],
                coupon: null,
            },
            loadingAddress: false,
            consultationResults: [],
            subscriptionData: [],
            loadingSubscription: true,
            showProcessingNotice: false,
            showErrorMessage: false,
            showSuccessMessage: false,
            messageMessage: '',
            shopifyId: undefined,
            productData: [],
            editingSubscription: undefined,
            showLoaderOverlay: false
        };
    }

    showPageLoader(value) {
        this.setState({
            showLoaderOverlay: value
        });
    }

    setEditingSubscription(sub) {
        this.setState({
            editingSubscription: sub
        });
        if (typeof sub === "undefined") {
            this.removeValueFromLocalStorage('editSub');
        } else {
            this.addValueToLocalStorage(JSON.stringify(sub), 'editSub');
        }
    }

    setShowErrorMessage(value, message) {
        this.setState({
            showErrorMessage: value,
            messageMessage: message
        });
    }

    setShowSuccessMessage(value, message) {
        this.setState({
            showSuccessMessage: value,
            messageMessage: message
        });
    }

    showDefaultErrorMessage() {
        this.setState({
            showErrorMessage: true,
            messageMessage: 'Something has gone wrong, please contact customer services'
        }, function () {
            setTimeout( () => {
                this.setState({
                    showErrorMessage: false,
                    messageMessage: ''
                })
            }, 3000 );
        });
    }

    /**
     * Extend default fetch call to add retries
     * @param url
     * @param options
     * @param retries
     * @returns {Promise<*>}
     */
    fetchWithRetries(url, options = {}, retries) {
        let that = this;
        return fetch(url, options)
            .then(response => {
               if (response.ok) {
                   return response.json();
               }
               if (retries > 0) {
                   console.log('retries', retries)
                   return that.fetchWithRetries(url, options, (retries - 1));
               }
                this.setState({
                    customerLoadError: true,
                    loadingCustomerData: false
                });
            });
    }

    getToken() {
        let token = this.state.token;
        if (typeof window !== "undefined" && typeof token == "undefined") {
            token = window.localStorage.getItem('token');
        }
        return token;
    }

    addValueToLocalStorage(value, key) {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(key, value);
        }
    }

    getValueFromLocalStorage(key) {
        if (typeof window !== "undefined") {
            return window.localStorage.getItem(key);
        }
        return '';
    }

    removeValueFromLocalStorage(key) {
        if (typeof window !== "undefined") {
            window.localStorage.removeItem(key);
        }
    }

    resetEditingSubscriptionOnReload() {
        let editSub = this.getValueFromLocalStorage('editSub');
        if (typeof editSub !== "undefined") {
            editSub = JSON.parse(editSub);
            this.setState({
                editingSubscription: editSub
            });
        }
    }

    /**
     *  Confirm customer and fetch customer token
     */
    async preloadCustomerToken(data) {
        if (this.state.customer !== undefined) {
            return;
        }
        if (typeof data === "undefined" && typeof window !== "undefined") {
            data = this.getValueFromLocalStorage('dataString');
        }
        if (typeof window !== "undefined" && typeof data !== "undefined" && data !== "" && data !== null) {
            window.localStorage.setItem('dataString', data);
            this.addValueToLocalStorage(data, 'dataString');
        }
        let that = this;
        // const shopifyId = this.props.shopifyId;
        // const email = this.props.email;
        // const data = this.props.data;
        const hostUrl = getHostUrl();
        let url = hostUrl + 'barber/account/app/customer/confirm?data=' + data;
        this.fetchWithRetries(url, {
            method: 'GET',
            headers: new Headers({
                'AccountAppCustomerConfirm' :  'confirm',
                'Content-Type' : 'application/json'
            })
        }, 5)
            .then(data => {
                if (data.success === true && data.code === "ACCOUNT_CONFIRMED") {
                    this.setState({
                        token: data.token,
                        loadingCustomerData: false,
                        customer: data.id,
                        customerLoadError: false
                    }, function() {
                        if  (typeof window !== "undefined") {
                            window.localStorage.setItem('token', data.token);
                        }
                        that.fetchCustomerDetails();
                    });
                }
                if (data.error === true) {
                    // retry the customer token load by loadCustomerRetries times
                    // before showing error
                    if (that.loadCustomerRetries > 0) {
                        that.loadCustomerRetries = that.loadCustomerRetries - 1;
                        that.preloadCustomerToken();
                    }
                    this.setState({
                       customerLoadError: true,
                       loadingCustomerData: false
                    });
                }

            }).catch(error => {
                console.log(error);
        });
    }

    /**
     *  Fetch customer details
     */
    fetchCustomerDetails(reload = false) {
        let that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/customer_entities/' + this.state.customer;

        let token = this.getToken();
        this.fetchWithRetries(url, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        }, 5)
            .then(data => {
               // console.log(data);
               if (typeof data !== "undefined" && typeof data['@type'] !== "undefined") {
                    let dob = data['dateOfBirth'];
                    if (typeof dob !== "undefined") {
                        let dobSplit = dob.split('T');
                        dob = dobSplit[0];
                        let dobString = dob.split('-');
                        dob = dobString[2] + '/' + dobString[1] + '/' + dobString[0];
                    } else {
                        dob = 'dd/mm/yyyy';
                    }
                    let addresses = data['addresses'];
                    let tmp = [];
                    if (typeof addresses !== "undefined" && addresses.length > 0) {
                        for(const address of addresses) {
                            let id = address.replace('/api/sales_order_addresses/', '');
                            tmp.push(id);
                        }
                    }

                    let details = {
                        dob: dob,
                        email: data['emailAddress'],
                        firstName: data['firstName'],
                        lastName: data['lastName'],
                        phone: data['phoneNumber'],
                    };
                    this.setState({
                        customerDetails: details,
                        shopifyId: data['shopify_customer_id']
                    }, function () {
                        if (tmp.length > 0) {
                            that.fetchAddresses(tmp);
                        }
                        that.fetchSubscriptionsForCustomer(reload)
                    });
                    return details;
                }
            })
            .catch(error => {
                console.log(error);
            })
    }

    /**
     *  Fetch customer addresses
     * @param ids
     */
    fetchAddresses(ids) {
        if (ids.length <= 0) {
            return {
                streetOne: '',
                streetTwo: '' ,
                city: '',
                country: '',
                postcode: '',
                county: '',
            };
        }
        const hostUrl = getHostUrl();
        let url = hostUrl + 'api/sales_order_addresses/';
        for (const id of ids) {
            if (url.includes('?')) {
                url += '&id[]=' + id;
            } else {
                url += '?id[]=' + id;
            }
        }
        let token = this.getToken();
        fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {

                let shippingAddress = {
                    streetOne: '',
                    streetTwo: '' ,
                    city: '',
                    country: '',
                    postcode: '',
                    county: '',
                };
                if (typeof data['@type'] !== "undefined") {
                    const addresses = data['hydra:member']
                    if (addresses?.[Symbol.iterator]) {
                        let hasFound = false;
                        for (const address of addresses) {
                            if (address['addressType'] === 'shipping' && address['isDefault'] === true) {
                                hasFound = true;
                                let streetTwo = address['street2'];
                                if (streetTwo === "null") {
                                    streetTwo = '';
                                }
                                shippingAddress = {
                                    id: address['id'],
                                    streetOne: address['street1'],
                                    streetTwo: streetTwo ,
                                    city: address['city'],
                                    country: address['country'],
                                    postcode: address['postcode'],
                                    county: address['county'],
                                };
                                break;
                            }
                        }

                        // Fall into backup address if we cannot find a default.
                        if (!hasFound) {
                            for (const address of addresses) {
                                if (address['addressType'] === 'shipping') {
                                    let streetTwo = address['street2'];
                                    if (streetTwo === "null") {
                                        streetTwo = '';
                                    }
                                    shippingAddress = {
                                        id: address['id'],
                                        streetOne: address['street1'],
                                        streetTwo: streetTwo,
                                        city: address['city'],
                                        country: address['country'],
                                        postcode: address['postcode'],
                                        county: address['county'] || '',
                                    };
                                    break;
                                }
                            }
                        }
                    }

                    this.setState({
                        customerAddress: shippingAddress
                    });
                }
                return shippingAddress;
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Update customer details and address
     * @param data
     */
    async updateDetails(data) {
        const hostUrl = getHostUrl();
        const customerUrl = hostUrl + 'api/customer_entities/' + this.state.customer;
        let customerData = {
            emailAddress: data['email'],
            firstName: data['firstName'],
            lastName: data['lastName'],
            phoneNumber: data['telephone'],
            fullName: (data['firstName'] + ' ' + data['lastName'])
        }
        let token = this.getToken();
        fetch(customerUrl, {
            method: 'PATCH',
            body: JSON.stringify(customerData),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(datum => {
                console.log(datum);
                if (typeof datum['@type'] !== "undefined") {
                    let dob = datum['dateOfBirth'];
                    if (typeof dob !== "undefined") {
                        let dobSplit = dob.split('T');
                        dob = dobSplit[0];
                        let dobString = dob.split('-');
                        dob = dobString[2] + '/' + dobString[1] + '/' + dobString[0];
                    } else {
                        dob = 'dd/mm/yyyy';
                    }
                    customerData = {
                        email: datum['emailAddress'],
                        firstName: datum['firstName'],
                        lastName: datum['lastName'],
                        phone: datum['phoneNumber'],
                        fullName: datum['fullName'],
                        dob: dob
                    }
                    this.setState({
                        customerDetails: customerData
                    });

                }
                return customerData;
            })
            .catch(error => {
                console.log(error);
            })

        const addressUrl = hostUrl + 'api/sales_order_addresses/' + this.state.customerAddress.id;

        let shippingData = {
            street1: data['streetOne'],
            street2: data['streetTwo'],
            city: data['city'],
            country: data['country'],
            postcode: data['postcode'],
            county: data['county'],
        };
        fetch(addressUrl, {
            method: 'PATCH',
            body: JSON.stringify(shippingData),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                // console.log(data);
                if (typeof data['@type'] !== "undefined") {
                    let streetTwo = data['street2'];
                    if (streetTwo === "null") {
                        streetTwo = '';
                    }
                    shippingData = {
                        id: data['id'],
                        streetOne: data['street1'],
                        streetTwo: streetTwo,
                        city: data['city'],
                        country: data['country'],
                        postcode: data['postcode']
                    };
                    this.setState({
                       customerAddress: shippingData
                    });
                }
            })
            .catch(error => {
                console.log(error);
            })
    }

    /**
     * Fetch customer consultation data
     * @returns {Promise<Awaited<[]|any>|*|[]|void>}
     */
    async fetchConsultationData() {
        // if we have already loaded the data, return this
        if (this.state.consultationResults.length > 0) {
            return Promise.resolve(this.state.consultationResults);
        }

        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/account/app/customer/consultation?shopify_id=' + this.state.shopifyId +
            '&customer_id=' + this.state.customer;
        let token = this.getToken();
        return fetch(url,{
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data?.success === true && data?.code === 'HAS_CONSULTATION_DATA') {
                    this.setState({
                        consultationResults: data.data
                    });
                    return data.data;
                }
                return [];
            })
            .catch(error => {
               console.log(error);
            });
    }

    /**
     * Fetch subscription for customer
     */
    async fetchSubscriptionsForCustomer(reload = false) {
        if (this.state.subscriptionData.length > 0 && !reload) {
            return;
        }
        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/account/app/customer/subscription?customer_id=' + this.state.customer;
        let token = this.getToken();
        return fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data?.success === true && data?.code === 'HAS_SUBSCRIPTIONS') {
                    let subs = data.data;
                    let isProcessing = false;
                    let editSub = this.getValueFromLocalStorage('editSub');
                    if (typeof editSub !== "undefined") {
                        editSub = JSON.parse(editSub);
                    }
                    if (subs.length > 1) {
                        for (const subData of subs) {
                            if (subData['data']['processingPause']) {
                                isProcessing = true;
                            }
                            if (editSub !== null && typeof editSub === "object" && typeof editSub['data'] !== "undefined" && subData['data']['id'] === editSub['data']['id']) {
                                editSub = subData;
                            }
                        }
                    } else {
                        for (const subData of subs) {
                            if (editSub !== null && typeof editSub === "object" && typeof editSub['data'] !== "undefined" && subData['data']['id'] === editSub['data']['id']) {
                                editSub = subData;
                                break;
                            }
                        }
                    }

                    this.setState({
                        subscriptionData: subs,
                        loadingSubscription: false,
                        showProcessingNotice: isProcessing
                    });
                    if (typeof editSub !== "undefined") {
                        this.addValueToLocalStorage(JSON.stringify(editSub), 'editSub');
                        this.setState({
                            editingSubscription: editSub
                        });
                    }
                    return data.data;
                }
                if (data?.error === true) {
                    this.setState({
                        loadingSubscription: false
                    });
                }
                return [];
            })
            .catch(error => {
                console.log(error);
            })
    }

    /**
     *  Pause Subscription
     * @param id
     * @param cancelPause
     * @returns {Promise<void>}
     */
    async pauseSubscription(id, cancelPause = false) {
        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/admin/update/pause?subscription_id=' + id +
            '&customer=' + this.state.customer + '&is_cancelled=' + cancelPause;
        let token = this.getToken();
        return fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data?.success === true && data?.code === 'SUBSCRIPTION_PAUSED') {
                    this.setState({
                        subscriptionData: data.data
                    });
                    return true;
                }
                if (data?.error === true) {
                    return "Something has gone wrong, please try again or contact customer services";
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Pause subscription with resumes date
     * @param id
     * @param date
     * @returns {Promise<boolean | string | void>}
     */
    async pauseSubscriptionWithResumes(id, date) {
        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/admin/update/pause?subscription_id=' + id +
            '&customer=' + this.state.customer + '&resumes_at=' + date;
        let token = this.getToken();
        return fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data?.success === true && data?.code === 'SUBSCRIPTION_PAUSED') {
                    this.setState({
                        subscriptionData: data.data
                    });
                    return true;
                }
                if (data?.error === true) {
                    return "Something has gone wrong, please try again or contact customer services";
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Resume subscription
     * @param id
     * @returns {Promise<void>}
     */
    async resumeSubscription(id) {
        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/admin/update/resume?subscription_id=' + id +
            '&customer=' + this.state.customer;
        let token = this.getToken();
        return fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data?.success === true && data?.code === 'SUBSCRIPTION_RESUMED') {
                    this.setState({
                        subscriptionData: data.data
                    });
                    return true;
                }
                if (data?.error === true) {
                    return "Something has gone wrong, please try again or contact customer services";
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Amend order date
     * @param id
     * @param date
     * @param subscription
     * @returns {Promise<undefined | * | [] | void>}
     */
    async amendOrderDate(id, date, subscription) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/sales_subscriptions/' + id;
        const datum = {
            next_cycle_date: date,
            stripe_subscription_id : subscription
        };
        let token = this.getToken();
        return fetch(url, {
            method: 'PATCH',
            body: JSON.stringify(datum),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if ((typeof data['@type'] !== "undefined" && data['@type'] === 'hydra:Error') || data?.error?.code === 400) {
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Remove coupon from Subscription
     * @param id
     * @returns {Promise<undefined | * | [] | void>}
     */
    async removeCoupons(id) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/sales_subscriptions/' + id;
        const datum = {
            coupons: [],
        };
        let token = this.getToken();
        return fetch(url, {
            method: 'PATCH',
            body: JSON.stringify(datum),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if ((typeof data['@type'] !== "undefined" && data['@type'] === 'hydra:Error') || data?.error?.code === 400) {
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Validate coupon before adding to subscription
     * @param code
     * @param id
     * @returns {Promise<* | string | void>}
     */
    async validateCoupon(code, id) {
        const that = this;
        const hostUrl = getHostUrl();
        const couponUrl = hostUrl + 'barber/account/app/coupon?couponCode=' + code + '&customerId=' + this.state.customer;
        let token = this.getToken();
        return fetch(couponUrl, {
            method: 'GET',
            headers: new Headers({
                'Barbershop' : 'front',
                'Token' : token,
                'Content-Type' : 'application/json',
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
               let message = 'Coupon not found'
                if (typeof data !== "undefined" && data.success === true) {
                    if (data.code === 'ALREADY_USED') {
                        message = 'You have already used this coupon';
                    } else if (data.code === 'USABLE_COUPON') {
                        let coupon = data['coupon_id'];
                        let couponString = coupon['coupon'];
                        coupon = '/api/sales_coupons/' + coupon;
                        return await that.addCoupon(coupon.toString(), id);
                    }
                }
                return message;
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Add coupon to Subscription
     * @param code
     * @param id
     * @returns {Promise<undefined | * | [] | void>}
     */
    async addCoupon(code, id) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/sales_subscriptions/' + id
        const datum = {
            coupons: [code],
        };
        let token = this.getToken();
        return fetch(url, {
            method: 'PATCH',
            body: JSON.stringify(datum),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
                if ((typeof data['@type'] !== "undefined" && data['@type'] === 'hydra:Error') || data?.error?.code === 400) {
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    /**
     * Change Subscription Frequency
     * @param id
     * @param products
     * @returns {Promise<undefined | * | [] | void>}
     */
    async changeFrequency(id, products) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/sales_subscriptions/' + id
        const datum = {
            products: products,
        };
        let token = this.getToken();
        return fetch(url, {
            method: 'PATCH',
            body: JSON.stringify(datum),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
                if ((typeof data['@type'] !== "undefined" && data['@type'] === 'hydra:Error') || data?.error?.code === 400) {
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    async fetchProductData() {
        const hostUrl = getHostUrl();
        const token = this.getToken();
        const url = hostUrl + 'barber/account/app/lookup/products';
        const request = new Request(url, {
            method: "GET",
            // body: JSON.stringify({ id: id }),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });
        return fetch(request, {

        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                // console.log(data);
                if (data.success === true) {
                    this.setState({
                        productsData: data.data
                    });
                    return data.data;
                }
                return [];
            })
            .catch (error => {
                console.log(error);
            })
    }

    async fetchCustomerProducts(id) {
        const token = this.getToken();
        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/product/plan/lookup';
        const request = new Request(url, {
            method: "POST",
            body: JSON.stringify({ id: id }),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });
        return fetch(request)
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (typeof data !== "undefined" && data.status === 'success'){
                    return data.data;
                }
                return [];
            })
            .catch(error => {
                console.log(error)
            })
    }

    async updateCustomerProducts(id, products) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/sales_subscriptions/' + id
        const datum = {
            products: products,
        };
        let token = this.getToken();
        return fetch(url, {
            method: 'PATCH',
            body: JSON.stringify(datum),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
                if ((typeof data['@type'] !== "undefined" && data['@type'] === 'hydra:Error') || data?.error?.code === 400) {
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    async updateConsultationTags(id, tag) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'barber/account/app/consultation/tags/?id=' + id + '&tag=' + tag +
            '&customerId=' + this.state.customer;

        let token = this.getToken();
        return fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    async updateHealthCheckTags() {
        const that = this;
        const hostUrl = getHostUrl();
        let url = hostUrl + 'barber/account/app/health/check/tags/?customerId=' + this.state.customer;

        let token = this.getToken();
        return fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
                if (typeof data !== "undefined" && data.error === true){
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    makeId(length) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < length) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    }

    async updateSubscriptionSurvey(data) {
        const that = this;
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/survey_forms'
        const date = new Date();

        let y = date.getFullYear();
        let m = date.getMonth() + 1;
        if (m < 10) {
            m = '0' + m;
        }
        let d = date.getDate();
        if (d < 10) {
            d  = '0' + d;
        }
        let dateString = y + '-' + m + '-' + d + 'T' + date.getHours() + ':' + date.getMinutes() + ':00';

        const datum = {
            json: JSON.stringify(data['json']),
            survey: 'api/surveys/' + data['survey'],
            emailAddress: data['customer_email'],
            externalId: that.makeId(13),
            customer: 'api/customer_entities/' + data['customer_id'],
            subscription: 'api/sales_subscriptions/' + data['subscription_id'],
            createdAt: dateString,
            updatedAt: dateString,
            surveyType: data['type']
        };
        let token = this.getToken();
        return fetch(url, {
            method: 'POST',
            body: JSON.stringify(datum),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token' :  token,
                'Barbershop' : 'front'
            })
        })
            .then(response => {
                return response.json();
            })
            .then(async data => {
                if ((typeof data['@type'] !== "undefined" && data['@type'] === 'hydra:Error') || data?.error?.code === 400) {
                    that.showDefaultErrorMessage();
                }
                return that.fetchSubscriptionsForCustomer(true);
            })
            .catch(error => {
                console.log(error);
            });
    }

    fetchCards = () => {
        const token = this.getToken();
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/customer/payment/card-list';
        const request = new Request(url, {
            method: "GET",
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });

        return fetch(request)
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (typeof data !== "undefined"){
                    this.setState({payment: {...this.state.payment, cards: data}});
                }
                return [];
            })
            .catch(error => {
                console.log(error)
            })
    }

    getActiveCard = () => {
        const cards = this.state.payment.cards;

        if (0 === cards.length) {
            return null;
        }

        return cards.find(card => true === card.isActive)
    }

    removeCard = (id) => {
        const token = this.getToken();
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/customer/payment/' + id;
        const request = new Request(url, {
            method: "DELETE",
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });

        return fetch(request)
            .then(response => {
                return response.json();
            })
            .catch(error => {
                console.log(error)
            })
    }

    getPaymentMethodIntent = () => {
        const token = this.getToken();
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/add-payment-method-intent';
        const request = new Request(url, {
            method: "GET",
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });

        return fetch(request)
            .then(response => {
                return response.json();
            })
            .then(data => data)
            .catch(error => {
                console.log(error)
            })
    }

    addCard = (cardholderName, cardNumber, expiryDate, cvc, makeDefault) => {
        const token = this.getToken();
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/customer/payment/add-card';
        const request = new Request(url, {
            method: "POST",
            body: JSON.stringify({cardholderName, cardNumber, expiryDate, cvc, makeDefault}),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });

        return fetch(request)
            .then(response => {
                return response.json();
            })
            .catch(error => {
                console.log(error)
            })
    }

    makeDefaultCard = (id) => {
        const token = this.getToken();
        const hostUrl = getHostUrl();
        const url = hostUrl + 'api/customer/payment/' + id + '/make-default';
        const request = new Request(url, {
            method: "POST",
            headers: new Headers({
                'Content-Type' : 'application/json',
                'Token':  token,
                'Barbershop' : 'front',
            })
        });

        return fetch(request)
            .then(response => {
                return response.json();
            })
            .catch(error => {
                console.log(error)
            })
    }

    getInactiveCards = () => {
        const cards = this.state.payment.cards;
        cards.filter(card => !card.isActive)

        return cards.filter(card => false === card.isActive)
    }

    render () {
        return (
            <CustomerContext.Provider value={ {
                ...this.state,
                fetchCustomerDetails: this.fetchCustomerDetails.bind(this),
                fetchAddresses: this.fetchAddresses.bind(this),
                updateDetails: this.updateDetails.bind(this),
                fetchConsultationData: this.fetchConsultationData.bind(this),
                fetchSubscriptionsForCustomer: this.fetchSubscriptionsForCustomer.bind(this),
                pauseSubscription: this.pauseSubscription.bind(this),
                resumeSubscription: this.resumeSubscription.bind(this),
                amendOrderDate: this.amendOrderDate.bind(this),
                removeCoupons: this.removeCoupons.bind(this),
                validateCoupon: this.validateCoupon.bind(this),
                changeFrequency: this.changeFrequency.bind(this),
                setShowErrorMessage: this.setShowErrorMessage.bind(this),
                setShowSuccessMessage: this.setShowSuccessMessage.bind(this),
                preloadCustomerToken: this.preloadCustomerToken.bind(this),
                fetchProductData: this.fetchProductData.bind(this),
                fetchCustomerProducts: this.fetchCustomerProducts.bind(this),
                updateCustomerProducts: this.updateCustomerProducts.bind(this),
                updateConsultationTags: this.updateConsultationTags.bind(this),
                updateSubscriptionSurvey: this.updateSubscriptionSurvey.bind(this),
                pauseSubscriptionWithResumes: this.pauseSubscriptionWithResumes.bind(this),
                setEditingSubscription: this.setEditingSubscription.bind(this),
                updateHealthCheckTags: this.updateHealthCheckTags.bind(this),
                showPageLoader: this.showPageLoader.bind(this),
                fetchCards: this.fetchCards.bind(this),
                removeCard: this.removeCard.bind(this),
                getPaymentMethodIntent: this.getPaymentMethodIntent.bind(this),
                addCard: this.addCard.bind(this),
                makeDefaultCard: this.makeDefaultCard.bind(this),
                getActiveCard: this.getActiveCard.bind(this),
                getInactiveCards: this.getInactiveCards.bind(this),
            } } >
                { this.props.children }
            </CustomerContext.Provider>
        );
    }
}
export default CustomerContextProvider;